import { ApiError, apiFetch } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import type {
  SupplierActiveQuotationSetting,
  BootstrapSupplierQuotationPayload,
  CreateSupplierQuotationItemPayload,
  CreateSupplierQuotationPayload,
  SupplierDailyQuotation,
  SupplierDailyQuotationPage,
  SupplierQuotationItem,
  SupplierQuotationItemPage,
  SupplierQuotationSubmissionPage,
  UpdateSupplierQuotationPayload,
  UpdateSupplierQuotationItemPayload,
} from "./types";

export function listSupplierQuotations(
  limit = 2000,
  offset = 0,
): Promise<SupplierDailyQuotationPage> {
  return apiFetch(
    `${ENDPOINTS.supplierQuotations.list}?limit=${limit}&offset=${offset}`,
  );
}

export async function getTodaySupplierQuotation(): Promise<SupplierDailyQuotation | null> {
  try {
    return await apiFetch<SupplierDailyQuotation>(
      ENDPOINTS.supplierQuotations.today,
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function getActiveSupplierQuotation(): Promise<SupplierActiveQuotationSetting | null> {
  try {
    return await apiFetch<SupplierActiveQuotationSetting>(
      ENDPOINTS.supplierQuotations.active,
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export function getSupplierQuotation(
  quotationId: string,
): Promise<SupplierDailyQuotation> {
  return apiFetch(ENDPOINTS.supplierQuotations.detail(quotationId));
}

export function updateSupplierQuotation(
  quotationId: string,
  payload: UpdateSupplierQuotationPayload,
): Promise<SupplierDailyQuotation> {
  return apiFetch(ENDPOINTS.supplierQuotations.detail(quotationId), {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function createSupplierQuotation(
  payload: CreateSupplierQuotationPayload,
): Promise<SupplierDailyQuotation> {
  return apiFetch(ENDPOINTS.supplierQuotations.list, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function bootstrapSupplierQuotation(
  sourceId: string,
  payload: BootstrapSupplierQuotationPayload,
): Promise<SupplierDailyQuotation> {
  return apiFetch(ENDPOINTS.supplierQuotations.bootstrap(sourceId), {
    method: "POST",
    body: JSON.stringify({ target_date: payload.targetDate }),
  });
}

export function setActiveSupplierQuotation(
  quotationId: string | null,
): Promise<SupplierActiveQuotationSetting> {
  return apiFetch(ENDPOINTS.supplierQuotations.active, {
    method: "PUT",
    body: JSON.stringify({ quotation_id: quotationId || "" }),
  });
}

export function listSupplierQuotationSubmissions(
  date: string,
  limit = 2000,
  offset = 0,
): Promise<SupplierQuotationSubmissionPage> {
  const normalizedDate = normalizeSupplierSubmissionDate(date);
  const params = new URLSearchParams({
    date: normalizedDate,
    limit: String(limit),
    offset: String(offset),
  });
  return apiFetch(
    `${ENDPOINTS.supplierQuotations.submissions}?${params.toString()}`,
  );
}

export function normalizeSupplierSubmissionDate(date: string) {
  return date.includes("T") ? date.slice(0, 10) : date;
}

export function listSupplierQuotationItems(
  quotationId: string,
  limit = 2000,
  offset = 0,
): Promise<SupplierQuotationItemPage> {
  return apiFetch(
    `${ENDPOINTS.supplierQuotations.items(quotationId)}?limit=${limit}&offset=${offset}`,
  );
}

export function createSupplierQuotationItem(
  quotationId: string,
  payload: CreateSupplierQuotationItemPayload,
): Promise<SupplierQuotationItem> {
  return apiFetch(ENDPOINTS.supplierQuotations.items(quotationId), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateSupplierQuotationItem(
  quotationId: string,
  itemId: string,
  payload: UpdateSupplierQuotationItemPayload,
): Promise<SupplierQuotationItem> {
  return apiFetch(ENDPOINTS.supplierQuotations.item(quotationId, itemId), {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteSupplierQuotationItem(
  quotationId: string,
  itemId: string,
): Promise<void> {
  return apiFetch(ENDPOINTS.supplierQuotations.item(quotationId, itemId), {
    method: "DELETE",
  });
}
